import { DatePicker, Tooltip } from "antd";
import {
  AlertTriangle,
  Award,
  Book,
  BookOpen,
  Calendar,
  CalendarDaysIcon,
  CalendarIcon,
  CheckCircle,
  ChevronRight,
  Clock,
  FolderOpen,
  Library,
  Mail,
  Phone,
  PlayCircle,
  Star,
  User,
  UserIcon,
  Video,
  VideoOff,
  X,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import { Input } from "../../../components/ui/input";
import isBetween from "dayjs/plugin/isBetween";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { BaseUrlApi, ErrorMessage } from "../../../lib/api";
import { toast } from "sonner";
import { Skeleton } from "src/components/ui/skeleton";
import { DateFormate } from "../../../lib/utils";
import { Button } from "../../../components/ui/button";
import { RatingDialog } from "./../../../components/common/RatingDialog";
import { useAPI } from "../../../context/APIContext";
dayjs.extend(isBetween);

const CourseDetails = () => {
  const { state } = useLocation();
  const courseId = state?.course?.id;

  const { studentCoursesItems } = useAPI();
  const { studentCourses, loadingStudentCourses } = studentCoursesItems;
  const course = studentCourses.find((item) => item.id === courseId);

  const firstAvailableGroupId =
    course?.levels?.find((item) => item.groups_count !== 0)?.groups?.[0]?.id ||
    null;

  const [activeLevel, setActiveLevel] = useState(firstAvailableGroupId);
  const [titleFilter, setTitleFilter] = useState("");
  const [dateRange, setDateRange] = useState([null, null]);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [open, setOpen] = useState(false);

  const [loadingLessons, setLoadingLessons] = useState(false);
  const [lessons, setLessons] = useState([]);

  const getAllLessons = async () => {
    try {
      const { data } = await axios.get(
        `${BaseUrlApi}/group/${activeLevel}/lessons`
      );
      setLessons(data.data);
    } catch (error) {
      toast.error("Error fetching lessons", {
        description: ErrorMessage(error),
      });
    } finally {
      setLoadingLessons(false);
    }
  };

  useEffect(() => {
    if (activeLevel) {
      setLoadingLessons(true);
      getAllLessons();
    }
  }, [activeLevel]);

  const filteredLessons = lessons
    .filter((lesson) => lesson?.group?.id === activeLevel)
    .filter((lesson) => {
      if (!titleFilter) return true;
      return lesson?.title?.en
        ?.toLowerCase()
        ?.includes(titleFilter.toLowerCase());
    })
    .filter((lesson) => {
      const [start, end] = dateRange;
      if (!start || !end) return true;
      const created = dayjs(
        lesson?.booking?.teacher_availability_id?.start_date
      );
      return created.isBetween(
        start.startOf("day"),
        end.endOf("day"),
        "day",
        "[]"
      );
    });

  const clearDateRange = () => {
    setDateRange([null, null]);
    setCalendarOpen(false);
  };

  const isComplete =
    lessons.length > 0 &&
    lessons.filter(
      (item) => item?.all_assignments_answered && item?.all_quizzes_attempted
    );

  useEffect(() => {
    if (course && !course?.rate && isComplete.length === lessons.length) {
      setOpen(true);
    } else {
      setOpen(false);
    }
  }, [lessons, course]);

  if (loadingStudentCourses) return <Skeleton className="h-24 w-full" />;

  return (
    <div className="">
      <CourseHeader course={course} setOpen={setOpen} />

      <div className="py-8">
        <div className="flex flex-col-reverse lg:grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Filters */}
            <div className="mb-4 flex gap-4 items-center flex-col sm:flex-row">
              <h2 className="text-2xl font-bold flex-1">Lessons</h2>

              <div className="flex gap-4 items-center w-full sm:w-1/2">
                <Input
                  placeholder="Search by title"
                  allowClear
                  value={titleFilter}
                  onChange={(e) => setTitleFilter(e.target.value)}
                  className="w-full"
                />

                <div className="relative flex items-center">
                  <CalendarDaysIcon
                    className="h-6 w-6 text-gray-600 cursor-pointer"
                    onClick={() => setCalendarOpen(true)}
                  />
                  {dateRange[0] && dateRange[1] && (
                    <X
                      className="h-4 w-4 cursor-pointer absolute -top-2 -end-2 bg-red-700 text-white rounded-full"
                      onClick={clearDateRange}
                    />
                  )}
                  <DatePicker.RangePicker
                    open={calendarOpen}
                    onOpenChange={(open) => setCalendarOpen(open)}
                    value={dateRange}
                    onChange={(dates) => setDateRange(dates)}
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      opacity: 0,
                      pointerEvents: "none",
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Lessons Content */}
            {loadingLessons ? (
              <Skeleton className="h-24 w-full" />
            ) : filteredLessons.length !== 0 ? (
              <LessonsList lessons={filteredLessons} />
            ) : (
              <div className="flex items-center justify-center gap-2 h-24 font-semibold">
                There is no lessons found <VideoOff />
              </div>
            )}
          </div>

          {/* Course Levels */}
          <div className="lg:col-span-1">
            <CourseLevels
              levels={course?.levels}
              activeLevel={activeLevel}
              setActiveLevel={setActiveLevel}
            />
          </div>
        </div>
      </div>

      <RatingDialog
        type={"course"}
        id={course?.id}
        open={open}
        onOpenChange={setOpen}
        apiUrl={`/reviews/course/${course?.id}`}
        title="Rate your course"
        description="How would you rate your course with us?"
      />
    </div>
  );
};

export default CourseDetails;

const CourseHeader = ({ course, setOpen }) => {
  return (
    <div className="relative">
      <div className="relative h-80 rounded-2xl">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-900/90 to-purple-900/70 rounded-2xl"></div>
        <img
          src="https://images.pexels.com/photos/4050315/pexels-photo-4050315.jpeg"
          alt={course?.title.en}
          className="w-full h-full object-cover rounded-2xl"
        />

        <div className="absolute inset-0 flex flex-col justify-center px-4 md:px-8 lg:px-16 container mx-auto">
          <div className="hidden sm:flex items-center flex-wrap gap-1 text-sm text-gray-200 mb-4">
            <Library className="h-4 w-4" />
            <span>{course?.sub_category.category.title.en}</span>

            <ChevronRight className="h-5 w-5" />

            <FolderOpen className="h-4 w-4" />
            <span>{course?.sub_category.title.en}</span>
          </div>

          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
            {course?.title.en}
          </h1>

          <p className="text-gray-200 text-lg max-w-2xl mb-6">
            {course?.description.en}
          </p>

          <div className="flex flex-wrap gap-4 text-white">
            <div className="flex items-center gap-2 bg-blue-700/50 px-3 py-1.5 rounded-full">
              <Book className="h-4 w-4" />
              <span>
                {
                  course?.levels.filter((level) => level.groups.length !== 0)
                    .length
                }{" "}
                Levels
              </span>
            </div>
            <div className="flex items-center">
              {course?.type === 0 ? (
                <>
                  <div className="flex items-center gap-2 bg-green-700/50 px-3 py-1.5 rounded-full">
                    <PlayCircle className="h-4 w-4" />
                    <span>Recorded</span>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex items-center gap-2 bg-red-700/50 px-3 py-1.5 rounded-full">
                    <Video className="h-4 w-4" />
                    <span>Live</span>
                  </div>
                </>
              )}
            </div>
            <TeacherInfo teacher={course?.created_by}>
              <div className="flex items-center gap-2 bg-purple-700/50 px-3 py-1.5 rounded-full">
                <Award className="h-4 w-4" />
                <span>By {course?.created_by.name}</span>
              </div>
            </TeacherInfo>
            <div
              className="flex items-center gap-1 bg-yellow-500/50 px-3 py-1.5 rounded-full cursor-pointer"
              onClick={() => setOpen(true)}
            >
              <div className="relative flex items-center">
                {!course?.rate && (
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-yellow-500 opacity-75" />
                )}
                <Star fill="#eab308" color="#eab308" className="h-4 w-4" />
              </div>
              <span className="mb-px">{Number(course?.rate)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const LessonsList = ({ lessons }) => {
  const navigate = useNavigate();

  return (
    <div className="space-y-4">
      {lessons.map((lesson) => (
        <div
          key={lesson.id}
          className={`
                  rounded-lg shadow-sm p-5 border-s-4 border
                  ${
                    lesson.all_assignments_answered &&
                    lesson.all_quizzes_attempted
                      ? "border-green-500"
                      : "border-yellow-500"
                  }
                `}
        >
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="flex-1 flex items-center gap-2">
              <span>
                {lesson.all_assignments_answered &&
                lesson.all_quizzes_attempted ? (
                  <Tooltip title="All tasks completed">
                    <CheckCircle className="text-green-600" />
                  </Tooltip>
                ) : (
                  <Tooltip title="Incomplete tasks">
                    <AlertTriangle className="text-yellow-600" />
                  </Tooltip>
                )}
              </span>
              <p className="text-lg font-semibold truncate">
                {lesson.title.en}
              </p>
            </div>

            <div className="flex items-center flex-1">
              <Calendar className="h-4 w-4 mr-2" />
              <Tooltip title="Start time">
                {DateFormate(
                  lesson?.booking?.teacher_availability_id?.start_date
                )}
              </Tooltip>
            </div>

            <Button
              className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
              onClick={() => navigate(`/lesson-view/${lesson.id}`)}
              disabled={lesson.status !== 1}
            >
              {"Details"}
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
};

const CourseLevels = ({ levels, activeLevel, setActiveLevel }) => {
  return (
    <div className="">
      <h2 className="text-xl font-bold mb-4">Course Levels</h2>

      <div className="space-y-3">
        {levels.map(
          (level) =>
            level.groups.length !== 0 && (
              <div
                key={level.id}
                onClick={() => setActiveLevel(level.groups[0].id)}
                className={`
                  p-4 rounded-lg cursor-pointer transition-all duration-300 border
                  ${
                    activeLevel === level.groups[0].id
                      ? "border-s-4 border-blue-600"
                      : " hover:bg-muted"
                  }
                `}
              >
                <div className="flex justify-between items-center">
                  <h3 className="font-medium">{level.title.en}</h3>
                  <ChevronRight
                    className={`h-5 w-5 transition-transform duration-300 ${
                      activeLevel === level.groups[0].id
                        ? "rotate-90 text-blue-600"
                        : "text-gray-400"
                    }`}
                  />
                </div>

                <p className="text-sm text-muted-foreground mt-1">
                  {level.description.en}
                </p>
              </div>
            )
        )}
      </div>
    </div>
  );
};

const TeacherInfo = ({ teacher, children }) => {
  const [showDetails, setShowDetails] = useState(false);

  const userImage = teacher?.image
    ? `https://example.com/${teacher?.image}`
    : `https://ui-avatars.com/api/?name=${encodeURIComponent(
        teacher?.name
      )}&background=random`;

  return (
    <div className="relative">
      <div
        onClick={() => setShowDetails(!showDetails)}
        className="flex items-center cursor-pointer group"
      >
        {children}
      </div>

      {showDetails && (
        <div className="absolute top-full left-0 mt-2 w-64 p-4 bg-background rounded-lg shadow-lg z-10 border animate-fadeIn">
          <button
            onClick={() => setShowDetails(false)}
            className="absolute top-2 right-2 text-gray-400 hover:text-muted-foreground"
          >
            <X className="h-4 w-4" />
          </button>

          <div className="flex items-center mb-3">
            <div className="w-12 h-12 rounded-full overflow-hidden border-2">
              <img
                src={userImage}
                alt={teacher?.name}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="ml-3">
              <p className="font-semibold text-primary">{teacher?.name}</p>
              <p className="text-xs text-blue-600">{teacher?.role}</p>
            </div>
          </div>

          <div className="space-y-2 text-sm">
            <div className="flex items-center text-muted-foreground">
              <Mail className="h-4 w-4 mr-2 text-gray-400" />
              <span className="truncate">{teacher?.email}</span>
            </div>
            <div className="flex items-center text-muted-foreground">
              <Phone className="h-4 w-4 mr-2 text-gray-400" />
              <span>{teacher?.phone}</span>
            </div>
            <div className="flex items-center text-muted-foreground">
              <UserIcon className="h-4 w-4 mr-2 text-gray-400" />
              <span>@{teacher?.user_name}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
